import { useDraggable, useDroppable } from '@dnd-kit/core';
import { calendarDaysBetween, melbourneDate } from '../../../shared/dates';
import { PHASE_LABELS, PHASE_PILL_LABELS, PHASE_PROGRESS, type ProjectPhase } from '../../../shared/types';
import { BrandBadge, Pill, formatProjectNumber } from '../../components/primitives';
import { cardDate, pillTone, waitingStatus } from './helpers';
import type { CardProject } from './types';

export function ProjectCardBody({
  p,
  today,
  onSendNow,
  onSnooze,
  waitingBusy = false,
  waitingNote = null,
}: {
  p: CardProject;
  today: string;
  onSendNow?: () => void;
  onSnooze?: () => void;
  waitingBusy?: boolean;
  waitingNote?: string | null;
}) {
  const date = cardDate(p, today);
  const progress = PHASE_PROGRESS[p.phase];
  const fill = p.phase === 'shoot' ? 'fill-gold' : p.phase === 'lead' ? 'fill-dim' : 'fill-accent';
  const overdueNext = p.next_task_due !== null && p.next_task_due < today;
  return (
    <>
      <div className="card-top">
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Pill tone={pillTone(p.phase)}>{PHASE_PILL_LABELS[p.phase]}</Pill>
          <BrandBadge brand={p.brand} />
          {p.voided_at !== null && <Pill tone="danger">Voided</Pill>}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {p.project_number !== null && (
            <span className="card-num">{formatProjectNumber(p.project_number)}</span>
          )}
          <span className={`card-date${date.hot ? ' hot' : ''}`}>{date.text}</span>
        </span>
      </div>
      <div className="card-title">{p.name}</div>
      <div className="card-client">{p.client_name}</div>
      {p.waiting_on_client_since !== null && (
        <div className="card-chip-row">
          <Pill tone="gold">
            ⏳ waiting on client · {Math.max(0, calendarDaysBetween(melbourneDate(p.waiting_on_client_since), today))}d
          </Pill>
        </div>
      )}
      {p.next_task_title ? (
        <div className="card-next">
          <span className="arrow">→</span>
          {overdueNext ? <span className="overdue">{p.next_task_title} — overdue</span> : p.next_task_title}
        </div>
      ) : (
        <div className="card-next none">{p.phase === 'on_hold' ? 'Paused' : 'No open tasks'}</div>
      )}
      <div className="card-footer">
        <span className="card-tasks">
          {p.phase === 'on_hold'
            ? 'Paused'
            : p.open_tasks === 0
              ? 'No open tasks'
              : `${p.open_tasks} open task${p.open_tasks === 1 ? '' : 's'}`}
        </span>
        <span className="card-progress">
          {progress !== null && <span className={fill} style={{ width: `${progress * 100}%` }} />}
        </span>
      </div>
      {p.waiting_on_client_since !== null && (
        <div className="card-nudge" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <span>Nudge:</span>
          <span className="chip">{waitingStatus(p, today)}</span>
          {onSendNow && (
            <button type="button" className="chip accent" disabled={waitingBusy} onClick={onSendNow}>
              {waitingBusy ? 'Sending…' : 'Send now'}
            </button>
          )}
          {onSnooze && (
            <button type="button" className="chip" disabled={waitingBusy} onClick={onSnooze}>
              +1w
            </button>
          )}
          {waitingNote && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{waitingNote}</span>}
        </div>
      )}
    </>
  );
}

function DraggableProjectCard({
  p,
  today,
  hidden,
  onOpen,
  onSendNow,
  onSnooze,
  waitingBusy,
  waitingNote,
}: {
  p: CardProject;
  today: string;
  hidden: boolean;
  onOpen: () => void;
  onSendNow: () => void;
  onSnooze: () => void;
  waitingBusy: boolean;
  waitingNote: string | null;
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({ id: p.id });
  const style = {
    ...(hidden ? { opacity: 0.35 } : {}),
    ...(p.phase === 'on_hold' ? { opacity: hidden ? 0.35 : 0.65 } : {}),
  };
  return (
    <div
      ref={setNodeRef}
      className="project-card"
      style={Object.keys(style).length ? style : undefined}
      onClick={onOpen}
      {...listeners}
      {...attributes}
    >
      <ProjectCardBody
        p={p}
        today={today}
        onSendNow={onSendNow}
        onSnooze={onSnooze}
        waitingBusy={waitingBusy}
        waitingNote={waitingNote}
      />
    </div>
  );
}

export function PhaseShelf({
  phase,
  dot,
  projects,
  today,
  draggingId,
  isOver,
  onOpen,
  onSendNow,
  onSnooze,
  waitingActionId,
  waitingNote,
}: {
  phase: ProjectPhase;
  dot: string;
  projects: CardProject[];
  today: string;
  draggingId: string | null;
  isOver: boolean;
  onOpen: (id: string) => void;
  onSendNow: (id: string) => void;
  onSnooze: (id: string) => void;
  waitingActionId: string | null;
  waitingNote: { id: string; text: string } | null;
}) {
  const { setNodeRef } = useDroppable({ id: phase });
  const showSlot = isOver && !projects.some((p) => p.id === draggingId);
  return (
    <div className="shelf" ref={setNodeRef}>
      <div className="shelf-head">
        <span className={`phase-dot ${dot}`} />
        <span className="shelf-title">{PHASE_LABELS[phase]}</span>
        <span className="shelf-count">{projects.length}</span>
      </div>
      {projects.length === 0 && !showSlot ? (
        <div className="empty-shelf">Nothing here.</div>
      ) : (
        <div className="shelf-cards wide">
          {showSlot && <div className="drop-slot tall" />}
          {projects.map((p) => (
            <DraggableProjectCard
              key={p.id}
              p={p}
              today={today}
              hidden={p.id === draggingId}
              onOpen={() => onOpen(p.id)}
              onSendNow={() => onSendNow(p.id)}
              onSnooze={() => onSnooze(p.id)}
              waitingBusy={waitingActionId === p.id}
              waitingNote={waitingNote?.id === p.id ? waitingNote.text : null}
            />
          ))}
        </div>
      )}
    </div>
  );
}
